import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  Chip,
  Alert,
  Skeleton,
  Button,
  Stack,
  useTheme,
  Collapse,
  Switch,
  FormControlLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  IconButton,
  TextField,
  MenuItem,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SearchIcon from "@mui/icons-material/Search";
import { APP_INVENTORY_API_BASE } from "../../../../config/api";
// Icons
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import DevicesIcon from "@mui/icons-material/Devices";
import HistoryIcon from "@mui/icons-material/History";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useAuth } from "../../../../auth/useAuth";

const API_BASE = `${APP_INVENTORY_API_BASE}`;

// ─── Unbind confirm dialog ────────────────────────────────────────────────────

function UnbindDialog({ open, binding, token, activeOrg, onClose, onUnbound }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleUnbind = async () => {
    setSubmitting(true);
    setError("");
    try {
      await axios.post(
        `${API_BASE}/bindings/${binding.id}/unbind`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
          },
        },
      );
      onUnbound(binding.id);
    } catch (err) {
      setError(err.response?.data?.error || "Unbind failed");
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle fontWeight={700}>Unbind asset?</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText>
          Asset <strong>{binding?.inventory_asset_id?.slice(0, 8)}…</strong>{" "}
          will be unbound from device{" "}
          <strong>
            {binding?.device_name || binding?.device_id?.slice(0, 8)}…
          </strong>
          . Status will return to <strong>IN_STOCK</strong>.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          disableElevation
          disabled={submitting}
          onClick={handleUnbind}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <LinkOffIcon />
            )
          }
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {submitting ? "Unbinding…" : "Unbind"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Custom toolbar ───────────────────────────────────────────────────────────

function CustomToolbar() {
  return (
    <GridToolbarContainer
      sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
    >
      <Box display="flex" gap={1}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport csvOptions={{ fileName: "device_bindings" }} />
      </Box>
    </GridToolbarContainer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

export default function BindingsList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { permissions, activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");

  // ── State ──
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bindings, setBindings] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [ownerTypeFilter, setOwnerTypeFilter] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [activeOnly, setActiveOnly] = useState(true);
  const [successAlert, setSuccessAlert] = useState("");
  const [unbindTarget, setUnbindTarget] = useState(null);
  const [stats, setStats] = useState({ active: 0, total: 0, loading: true });
  const [searchQuery, setSearchQuery] = useState("");
  const [ownershipStateFilter, setOwnershipStateFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const hasPermission = (p) => {
    if (!permissions || permissions.size === 0) return false;
    if (permissions.has("*")) return true;
    return permissions.has(p);
  };

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  const showSuccess = (msg) => {
    setSuccessAlert(msg);
    setTimeout(() => setSuccessAlert(""), 5000);
  };

  // ── Fetch bindings ──
  const fetchBindings = useCallback(async () => {
    if (!hasPermission("inventory.binding.view")) return;
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...(activeOnly && { active: "true" }),
      };
      const res = await axios.get(`${API_BASE}/bindings`, {
        headers: authHeaders,
        params,
      });
      setBindings(res.data.data || []);
      setRowCount(res.data.meta?.pagination?.total_items || 0);
      setError(null);
    } catch {
      setError("Failed to load bindings.");
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg, paginationModel, activeOnly]);
  const fetchStatusOptions = async () => {
    try {
      const res = await axios.get(`${API_BASE}/bindings/status-options`, {
        headers: authHeaders,
      });

      setStatusOptions(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch status options");
    }
  };

  useEffect(() => {
    fetchStatusOptions();
  }, []);
  useEffect(() => {
    fetchBindings();
  }, [fetchBindings]);

  // ── Fetch stats ──
  const fetchStats = useCallback(() => {
    if (!hasPermission("inventory.binding.view")) return;
    setStats((prev) => ({ ...prev, loading: true }));
    Promise.all([
      axios.get(`${API_BASE}/bindings`, {
        headers: authHeaders,
        params: { per_page: 1, active: "true" },
      }),
      axios.get(`${API_BASE}/bindings`, {
        headers: authHeaders,
        params: { per_page: 1 },
      }),
    ])
      .then(([a, t]) =>
        setStats({
          active: a.data.meta?.pagination?.total_items ?? 0,
          total: t.data.meta?.pagination?.total_items ?? 0,
          loading: false,
        }),
      )
      .catch(() => setStats((prev) => ({ ...prev, loading: false })));
  }, [token, activeOrg]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleUnbound = (bindingId) => {
    setUnbindTarget(null);
    // Optimistically remove from active list if in active-only mode
    if (activeOnly) {
      setBindings((prev) => prev.filter((b) => b.id !== bindingId));
      setRowCount((prev) => prev - 1);
    } else {
      // Refresh to get updated unbound_at
      fetchBindings();
    }
    fetchStats();
    showSuccess("Asset unbound successfully. Status is now IN_STOCK.");
  };
  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // ── Columns ──
  const columns = [
    {
      field: "serialIndex",
      headerName: "S.No",
      width: 64,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {paginationModel.page * paginationModel.pageSize +
              params.api.getRowIndexRelativeToVisibleRows(params.id) +
              1}
          </Typography>
        </Box>
      ),
    },
    {
      field: "inventory_asset_id",
      headerName: "Asset",
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            color="primary.main"
            sx={{
              fontFamily: "monospace",
              fontSize: 12,
              "&:hover": { textDecoration: "underline" },
              cursor: "pointer",
            }}
          >
            {params.row.asset_serial_number || params.value?.slice(0, 8) + "…"}
          </Typography>
          {params.row.asset_type && (
            <Typography variant="caption" color="text.secondary">
              {params.row.asset_type}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "device_id",
      headerName: "Device",
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ fontFamily: "monospace", fontSize: 12 }}
          >
            {params.row.device_name ||
              params.row.device_identifier ||
              params.value?.slice(0, 8) + "…"}
          </Typography>
          {params.row.device_identifier && params.row.device_name && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              {params.row.device_identifier}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 110,
      renderCell: (params) =>
        params.value ? (
          <Chip
            label="Active"
            size="small"
            color="success"
            variant="outlined"
            icon={<LinkIcon />}
            sx={{ fontSize: 11 }}
          />
        ) : (
          <Chip
            label="Unbound"
            size="small"
            variant="outlined"
            sx={{ fontSize: 11, color: "text.secondary" }}
          />
        ),
    },
    {
      field: "bound_at",
      headerName: "Bound at",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            {params.value
              ? new Date(params.value).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "unbound_at",
      headerName: "Unbound at",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            {params.value
              ? new Date(params.value).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "_actions",
      headerName: "Actions",
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="View asset">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/inventory/assets/${params.row.inventory_asset_id}`);
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.is_active &&
            hasPermission("inventory.binding.remove") && (
              <Tooltip title="Unbind">
                <IconButton
                  size="small"
                  color="warning"
                  onClick={(e) => {
                    e.stopPropagation();
                    setUnbindTarget(params.row);
                  }}
                >
                  <LinkOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
        </Stack>
      ),
    },
  ];
  const hasActiveFilters =
    statusFilter || ownerTypeFilter || ownershipStateFilter || searchQuery;
  const normalize = (val) =>
    (val || "").toString().toLowerCase().replace(/_/g, " ").trim();
  // ── Guard ──
  if (!hasPermission("inventory.binding.view")) {
    return (
      <Box p={4}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          You do not have permission to view device bindings.
        </Alert>
      </Box>
    );
  }

  const statCards = [
    {
      title: "Active bindings",
      value: stats.active,
      icon: <LinkIcon color="success" />,
      bg: "#dcfce7",
    },
    {
      title: "Total ever bound",
      value: stats.total,
      icon: <HistoryIcon color="action" />,
      bg: "#f1f5f9",
    },
    {
      title: "Unbound",
      value: stats.total - stats.active,
      icon: <LinkOffIcon color="warning" />,
      bg: "#fff7ed",
    },
  ];
  const filteredBindings = bindings.filter((row) => {
    const query = normalize(searchText);

    return (
      normalize(row.inventory_asset_id).includes(query) ||
      normalize(row.device_name).includes(query) ||
      normalize(row.device_identifier).includes(query) ||
      normalize(row.asset_serial_number).includes(query) ||
      normalize(row.asset_type).includes(query) ||
      normalize(row.is_active ? "active" : "unbound").includes(query) ||
      normalize(row.bound_at).includes(query) ||
      normalize(row.unbound_at).includes(query)
    );
  });
  return (
    <Box
      sx={{
        minHeight: "100%",
        pt: { xs: 5, sm: 5, md: 3 },
        pb: 4,
        px: { xs: 2, md: 3 },

        background:
          "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #f8fbff 0%, #eef4f8 100%)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          mb: 4,
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 4 },
          borderRadius: 5,

          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

          boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
        }}
      >
        {/* ── Header ── */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          mb={4}
        >
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ color: "#e2f8f5" ,fontSize:{xs:28,md:40} }}>
              Device Bindings
            </Typography>
            <Typography variant="body2" color="#fff">
              Assets currently deployed on IoT devices.
            </Typography>
          </Box>
        </Stack>

        {/* ── Alerts ── */}
        <Collapse in={!!successAlert}>
          <Alert
            severity="success"
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setSuccessAlert("")}
          >
            {successAlert}
          </Alert>
        </Collapse>
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* ── Stat cards ── */}
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "1fr", // mobile → 1 card
            sm: "1fr 1fr", // tablet → 2 cards
            md: "1fr 1fr 1fr", // small laptop → 3 cards
            lg: "1fr 1fr 1fr", // desktop → 3 cards in one row
          }}
          gap={2}
          mb={1}
        >
          {stats.loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: 7,
                    p: 2,

                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    backdropFilter: "blur(12px)",

                    color: "#fff",

                    transition: "all 0.25s ease",

                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 15px 40px rgba(0,0,0,0.25)",
                    },
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={52}
                    height={52}
                    sx={{ mr: 2, borderRadius: 2 }}
                  />
                  <Box flex={1}>
                    <Skeleton variant="text" width="60%" sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="40%" height={32} />
                  </Box>
                </Card>
              ))
            : statCards.map((s, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    p: 2,

                    // 🔥 FIXED BACKGROUND (no more white)
                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    backdropFilter: "blur(12px)",

                    color: "#fff",

                    transition: "all 0.25s ease",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 2,
                        bgcolor: s.bg,
                        color: s.color,
                        mr: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Box>
                      <Typography variant="body2" color="#fff" fontWeight={500}>
                        {s.title}
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {Math.max(0, s.value).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
        </Box>
      </Box>
      {/* ── Table card ── */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 5,
          overflow: "hidden",

          // 🔥 glass theme
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.12)",
          backdropFilter: "blur(14px)",

          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        {/* Toolbar / toggle */}
        <Box
          p={2}
          borderBottom="1px solid"
          borderColor="divider"
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gap={2}
          sx={{
            p: 2,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,

            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

            borderBottom: "1px solid rgba(255,255,255,0.15)",
          }}
        >
           <Typography
              sx={{ fontWeight: 600, fontSize:{xs:15,md:20}, color: "#f3f4f7" }}
            >
            Bindings
            </Typography>
          {/* Status */}
          {/* <TextField
                    select
                    size="small"
                    label="Status"
                    value={statusFilter}
                    onChange={handleFilterChange(setStatusFilter)}
                    sx={{
                      width: { xs: "100%", sm: 170 },
                      borderRadius: 2,
        
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        background: "rgba(246, 243, 243, 0.08)",
                        color: "#fff",
        
                        // 🔥 DEFAULT BORDER
                        "& fieldset": {
                          borderColor: "rgba(255,255,255,0.3)",
                        },
        
                        // 🔥 HOVER BORDER
                        "&:hover fieldset": {
                          borderColor: "#38BDF8", // change if needed
                        },
        
                        // 🔥 FOCUSED BORDER (removes blue)
                        "&.Mui-focused fieldset": {
                          borderColor: "#22C55E", // your custom focus color
                          borderWidth: "1px",
                        },
                      },
        
                      // 🔥 LABEL COLOR
                      "& .MuiInputLabel-root": {
                        color: "rgba(255,255,255,0.7)",
                      },
        
                      // 🔥 LABEL FOCUS COLOR (removes blue)
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#fff",
                      },
        
                      // 🔥 DROPDOWN ARROW
                      "& .MuiSelect-icon": {
                        color: "#fff",
                      },
        
                      // 🔥 LEFT ICON
                      "& .MuiInputAdornment-root svg": {
                        color: "#fff",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FilterListIcon fontSize="small" sx={{ color: "#fff" }} />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="">All statuses</MenuItem>
                    <MenuItem value="IN_STOCK">Active</MenuItem>
                    <MenuItem value="DEPLOYED">Inactive</MenuItem>
                   
                  </TextField> */}
          {/* Clear filters */}
          {/* {hasActiveFilters && (
                    <Button
                      size="small"
                    
                      variant="outlined"
                      onClick={() => {
                        setStatusFilter("");
                        setOwnerTypeFilter("");
                        setOwnershipStateFilter("");
                        setSearchQuery("");
                        setPaginationModel((prev) => ({ ...prev, page: 0 }));
                      }}
                       sx={{
    textTransform: "none",
    fontSize: 12,
    height: 38,

    color: "#fff", // text color
    borderColor: "rgba(255,255,255,0.4)", // border

    "&:hover": {
      borderColor: "#fff",
      backgroundColor: "rgba(255,255,255,0.08)",
    },
  }}
                    >
                      Clear filters
                    </Button>
                  )} */}
          <TextField
            size="small"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
           sx={{
              width: { xs: "100%", sm: 360 },
              ml: "auto",
              borderRadius: 2,

              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiSelect-icon": { color: "rgba(203, 213, 225, 0.70)" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#fff" }} />
                </InputAdornment>
              ),

              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchText("");
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                    sx={{ color: "#fff" }}
                  >
                    ✕
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* <FormControlLabel
            control={
              <Switch
                checked={activeOnly}
                size="small"
                onChange={(e) => {
                  setActiveOnly(e.target.checked);
                  setPaginationModel((p) => ({ ...p, page: 0 }));
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
              >
                {activeOnly ? "Showing active only" : "Showing full history"}
              </Typography>
            }
          /> */}
        </Box>

        {/* DataGrid */}
        <Box
          sx={{
            height: bindings.length === 0 && !loading ? 320 : 580,
            width: "100%",
          }}
        >
          <DataGrid
            rows={filteredBindings}
            rowCount={rowCount}
            loading={loading}
            paginationMode="server"
            pageSizeOptions={[15, 20, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            columns={columns}
            getRowId={(row) => row.id}
            onRowClick={(params) => {
              if (params.field === "_actions") return;
              navigate(`/inventory/assets/${params.row.inventory_asset_id}`);
            }}
            slots={{
              toolbar: CustomToolbar,
              noRowsOverlay: () => (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  py={6}
                >
                  <LinkIcon
                    sx={{ fontSize: 52, color: "text.disabled", mb: 2 }}
                  />
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="text.secondary"
                    gutterBottom
                  >
                    {activeOnly
                      ? "No active bindings"
                      : "No bindings recorded yet"}
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    {activeOnly
                      ? "No assets are currently deployed — bind an asset from the asset detail page"
                      : "Bindings appear here when assets are bound to devices"}
                  </Typography>
                </Box>
              ),
            }}
            disableRowSelectionOnClick
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f8fafc",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                color: "text.secondary",
                fontSize: 12,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#334155" : "#f8fafc",
                cursor: "pointer",
              },
              "& .MuiDataGrid-cell:focus": { outline: "none" },
              "& .MuiDataGrid-cell:focus-within": { outline: "none" },
            }}
          />
        </Box>
      </Card>

      {/* ── Unbind dialog ── */}
      {unbindTarget && (
        <UnbindDialog
          open={!!unbindTarget}
          binding={unbindTarget}
          token={token}
          activeOrg={activeOrg}
          onClose={() => setUnbindTarget(null)}
          onUnbound={handleUnbound}
        />
      )}
    </Box>
  );
}
